import {redactHeaders, redactQuery, redactSensitive, redactText, redactUrl} from './redactLogData'

interface ISentryRequestLike {
  headers?: unknown
  url?: unknown
  query_string?: unknown
  cookies?: unknown
  data?: unknown
}

interface ISentrySpanLike {
  data?: unknown
  description?: string
}

interface ISentryExceptionLike {
  value?: string
}

export interface ISentryEventLike {
  request?: ISentryRequestLike
  transaction?: string
  spans?: ISentrySpanLike[]
  contexts?: Record<string, unknown> & {trace?: {data?: unknown}}
  breadcrumbs?: ISentryBreadcrumbLike[]
  extra?: unknown
  message?: string
  exception?: {values?: ISentryExceptionLike[]}
  logentry?: {message?: string}
}

export interface ISentryBreadcrumbLike {
  message?: string
  data?: unknown
}

export interface ISentryLogLike {
  message: string
  attributes?: unknown
}

export function redactSentryEvent<T extends ISentryEventLike>(event: T): T {
  try {
    return redactSentryEventSections(event)
  } catch {
    return stripSentryEventSections(event)
  }
}

export function redactSentryBreadcrumb<T extends ISentryBreadcrumbLike>(breadcrumb: T): T | null {
  try {
    if(typeof breadcrumb.message === 'string') {
      breadcrumb.message = redactText(breadcrumb.message)
    }
    if(breadcrumb.data !== undefined) {
      breadcrumb.data = redactSensitive(breadcrumb.data)
    }
    return breadcrumb
  } catch {
    return null
  }
}

export function redactSentryLog<T extends ISentryLogLike>(log: T): T | null {
  try {
    if(typeof log.message === 'string') {
      log.message = redactText(log.message)
    }
    if(log.attributes !== undefined) {
      log.attributes = redactSensitive(log.attributes)
    }
    return log
  } catch {
    return null
  }
}

function redactSentryEventSections<T extends ISentryEventLike>(event: T): T {
  if(typeof event.transaction === 'string') {
    event.transaction = redactText(event.transaction)
  }
  if(event.request) {
    event.request = redactSentryRequest(event.request)
  }
  if(Array.isArray(event.spans)) {
    for(const span of event.spans) {
      redactSentrySpan(span)
    }
  }
  if(event.contexts?.trace && event.contexts.trace.data !== undefined) {
    event.contexts.trace.data = redactSensitive(event.contexts.trace.data)
  }
  if(Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs
      .map((breadcrumb) => redactSentryBreadcrumb(breadcrumb))
      .filter((breadcrumb): breadcrumb is ISentryBreadcrumbLike => breadcrumb !== null)
  }
  if(event.extra !== undefined) {
    event.extra = redactSensitive(event.extra)
  }
  if(typeof event.message === 'string') {
    event.message = redactText(event.message)
  }
  if(Array.isArray(event.exception?.values)) {
    for(const exception of event.exception.values) {
      if(typeof exception.value === 'string') {
        exception.value = redactText(exception.value)
      }
    }
  }
  if(typeof event.logentry?.message === 'string') {
    event.logentry.message = redactText(event.logentry.message)
  }
  return event
}

function stripSentryEventSections<T extends ISentryEventLike>(event: T): T {
  delete event.request
  delete event.breadcrumbs
  delete event.extra
  delete event.spans
  if(event.contexts?.trace) {
    delete event.contexts.trace.data
  }
  if(typeof event.message === 'string') {
    event.message = redactText(event.message)
  }
  if(Array.isArray(event.exception?.values)) {
    for(const exception of event.exception.values) {
      if(typeof exception.value === 'string') {
        exception.value = redactText(exception.value)
      }
    }
  }
  return event
}

function redactSentryRequest(request: ISentryRequestLike): ISentryRequestLike {
  const redacted: ISentryRequestLike = {...request}
  if(redacted.headers !== undefined) {
    redacted.headers = redactHeaders(redacted.headers)
  }
  if(redacted.url !== undefined) {
    redacted.url = redactUrl(redacted.url)
  }
  if(redacted.query_string !== undefined) {
    redacted.query_string = redactQuery(redacted.query_string)
  }
  delete redacted.cookies
  delete redacted.data
  return redacted
}

function redactSentrySpan(span: ISentrySpanLike): void {
  if(span.data !== undefined) {
    span.data = redactSensitive(span.data)
  }
  if(typeof span.description === 'string') {
    span.description = redactText(span.description)
  }
}
